import { Dialog, DIALOG_DATA } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ACADEMIC_YEAR_KEY } from '@app/config';
import { ActivityService, DailyService, NotificationService } from '@app/core';
import { isOnlineActivity } from '@app/core/models';
import { EclassWorkerEnum } from '@app/core/models/eclass-worker.enum';
import { ModalGamesComponent } from '@app/layouts/components/modal-games/modal-games.component';
import { SHARED_MODULES } from '@app/shared';
import { logoByProfile, notNull, safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { clone } from 'lodash';
import { map, Observable, switchMap, take, tap } from 'rxjs';
import { ModalTrailStudentComponent } from '../modal-trail-student/modal-trail-student.component';
import { ResourceComponentComponent } from '../resource-component/resource-component.component';
import { SummaryUploadComponent } from '../summary-upload/summary-upload.component';

@Component({
  imports: [SHARED_MODULES, ResourceComponentComponent, SummaryUploadComponent],
  templateUrl: './modal-view-daily.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalViewDailyComponent {
  public data = inject(DIALOG_DATA);
  private _store = inject(Store<AppState>);
  private _destroyRef = inject(DestroyRef);
  private _notificationService = inject(NotificationService);
  private _dailyService = inject(DailyService);
  private _activityService = inject(ActivityService);
  private _dialog = inject(Dialog);
  private _cdRef = inject(ChangeDetectorRef);

  public allowEdit$: Observable<boolean>;
  public isExternalHispanic$: Observable<boolean>;
  public logo: Signal<string>;
  public readonly QUERY = {
    [EclassWorkerEnum.Daily]: (id: number, data: any) => this._dailyService.update(id, data),
    [EclassWorkerEnum.Activity]: (id: string, data: any) =>
      this._activityService.changeStatus(id, { published: data.published }),
  };

  constructor() {
    this.logo = toSignal(
      this._store.select(AppSelectors.appFeature).pipe(
        takeUntilDestroyed(this._destroyRef),
        map((store) => logoByProfile(store)),
      ),
    );

    this.isExternalHispanic$ = this._store
      .select(AppSelectors.isExternalHispanic)
      .pipe(takeUntilDestroyed(this._destroyRef), notNull());
  }

  public get isActivity(): boolean {
    return this.data.worker === EclassWorkerEnum.Activity;
  }

  public get canShowUpload(): boolean {
    return this.isActivity && isOnlineActivity(this.data.element?.type);
  }

  public get title(): string {
    return this.isActivity ? 'geral.atividade' : 'geral.aula';
  }

  public showScore(element: any): string {
    if (element?.score) {
      return element?.answers?.score;
    }
    return `${element?.answers?.score}/${element?.score}`;
  }

  public onPublish(element, published: boolean): void {
    this.QUERY[this.data.worker](element.id, {
      published: published ? 1 : 0,
      date: element.date,
      group_id: element.group_id,
      subject_id: element.subject_id,
      entity_id: element?.entity_id,
    })
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        element.published = published;
        this._notificationService.success('geral.sucesso');
      });
  }

  public onOpenModal(dailyId: number | string, trailElements: any[]) {
    if (trailElements.length <= 0) {
      return;
    }

    this._dialog
      .open<ModalTrailStudentComponent, any>(ModalTrailStudentComponent, {
        data: {
          trailElements,
          dailyId,
        },
      })
      .closed.pipe(
        notNull(),
        tap(({ trail_elements }: any) => {
          const completedIds = new Set(trail_elements.filter((e) => e.completed === 1).map((e) => e.id));

          this.data.element = {
            ...this.data.element,
            trail_elements: this.data.element.trail_elements.map((te) => ({
              ...te,
              completed: completedIds.has(te.id) ? 1 : te.completed,
            })),
          };
        }),
      )
      .subscribe();
  }

  public onOpenModalElement(dailyId: number | string, trailElement: any) {
    this.onOpenModal(dailyId, [trailElement]);
  }

  public onOpenModalGame({ path, name }) {
    this._dialog
      .open(ModalGamesComponent, { data: { gamePath: path, gameTitle: name } })
      .closed.pipe(takeUntilDestroyed(this._destroyRef), notNull());
  }

  public getStyleTextIcon(type: string): string {
    switch (type) {
      case 'avaliacao':
        return 'text-base-trilhas-tipo-quiz';
      case 'imagem':
        return 'text-base-trilhas-tipo-image';
      case 'flip':
        return 'text-base-trilhas-tipo-ebook';
      case 'texto':
        return 'text-base-trilhas-tipo-text';
      default:
        return `text-base-trilhas-tipo-${type}`;
    }
  }

  public mapTypeToIcon(element: string): string {
    const type = element?.toLowerCase();

    const typeMapping: Record<string, string> = {
      audio: 'icon-headphone.svg',
      avaliacao: 'icon-book-pencil.svg',
      flip: 'icon-books.svg',
      imagem: 'icon-landscape.svg',
      link: 'icon-clip.svg',
      texto: 'icon-text-lines.svg',
      video: 'icon-film-alt.svg',
    };

    return typeMapping[type] || 'icon-help.svg';
  }

  public onRefresh() {
    this._store
      .select(AppSelectors.ActiveUser)
      .pipe(
        notNull(),
        take(1),
        switchMap(() =>
          this._activityService
            .show(this.data.element.id, {}, { [ACADEMIC_YEAR_KEY.year]: this.data.element.daily.year })
            .pipe(safeEmpty()),
        ),
      )
      .subscribe((activity) => {
        this.data = clone({ ...this.data, element: activity });
        this._cdRef.markForCheck();
      });
  }
}
